import { useEffect, useState } from "react";
import {
  CalendarDays,
  ClipboardList,
  Loader2,
  Users,
  User,
  MapPin,
  FileCheck,
  Phone,
  Layers,
  Heart,
  Home,
  Truck,
  FileText,
  Briefcase,
  Paperclip,
  CheckCircle2,
  XCircle,
  CircleDot,
  Hash,
  Globe,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Badge } from "@project/components/ui/badge";
import { tiffinSevaAdminService } from "@project/services/admin/tiffin-seva.service";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";

type TiffinSevaDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inquiryId: number | null;
  preview?: any;
};

function displayValue(value?: string | number | boolean | null) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return String(value);
}

function DetailField({
  icon: Icon,
  label,
  value,
  full = false,
}: {
  icon: any;
  label: string;
  value?: string | number | boolean | null;
  full?: boolean;
}) {
  return (
    <div className={`flex items-start gap-3 py-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <div className="text-blue-600 mt-1 shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block">
          {label}
        </span>
        <span className="text-sm font-semibold text-foreground mt-0.5 block leading-normal whitespace-pre-wrap">
          {displayValue(value)}
        </span>
      </div>
    </div>
  );
}

function DetailCard({
  icon: Icon,
  title,
  children,
}: {
  icon: any;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl border border-border/70 p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2.5 border-b border-border pb-3">
        <div className="text-blue-600">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="font-serif text-lg font-bold text-foreground leading-none">{title}</h3>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {children}
      </div>
    </div>
  );
}

export function TiffinSevaDetailDialog({
  open,
  onOpenChange,
  inquiryId,
  preview,
}: TiffinSevaDetailDialogProps) {
  const [data, setData] = useState<any | null>(preview ?? null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && inquiryId) {
      setLoading(true);
      tiffinSevaAdminService
        .getInquiry(inquiryId)
        .then((res) => {
          if (res) {
            setData(res);
          }
        })
        .catch((err) => {
          projectToast.error(getApiErrorMessage(err, "Failed to load details"));
        })
        .finally(() => setLoading(false));
    } else if (!open) {
      setData(null);
    }
  }, [open, inquiryId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto p-6 sm:p-8 rounded-3xl bg-[#FAF9F6]">
        <DialogHeader className="border-b border-border pb-4 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-3 text-2xl font-serif font-bold text-foreground">
              <ClipboardList className="h-6 w-6 text-blue-600" />
              <span>Tiffin Seva Application Details</span>
            </DialogTitle>
            {data && (
              <div className="flex items-center gap-2">
                <Badge
                  className={
                    data.application_status === "Approved" || data.application_status === "APPROVED"
                      ? "bg-emerald-100 hover:bg-emerald-100 text-emerald-800 border-transparent shadow-none font-bold rounded-full px-3 py-1 text-xs"
                      : data.application_status === "Rejected" || data.application_status === "REJECTED"
                        ? "bg-rose-100 hover:bg-rose-100 text-rose-800 border-transparent shadow-none font-bold rounded-full px-3 py-1 text-xs"
                        : "bg-amber-100 hover:bg-amber-100 text-amber-800 border-transparent shadow-none font-bold rounded-full px-3 py-1 text-xs"
                  }
                >
                  {(data.application_status || "PENDING").toUpperCase()}
                </Badge>
              </div>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
            <p className="text-xs">Loading application information...</p>
          </div>
        ) : !data ? (
          <p className="text-center text-xs text-muted-foreground py-10">
            No information available.
          </p>
        ) : (
          <div className="space-y-6">
            {/* TOP SUMMARY ROW */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 border-b border-border/80 pb-6 mb-6">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Tiffin Seva Category
                </span>
                <div className="text-lg font-bold text-foreground mt-1 font-serif">
                  {data.categorys || "—"}
                </div>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Start Date
                </span>
                <div className="text-sm font-bold text-foreground mt-1">
                  {data.tiffin_start_date || "—"}
                </div>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Mobile No
                </span>
                <div className="text-sm font-bold text-foreground mt-1 font-mono">
                  {data.mobile_number || "—"}
                </div>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Gender
                </span>
                <div className="text-sm font-bold text-foreground mt-1">
                  {data.gender || "—"}
                </div>
              </div>
            </div>

            {/* CARDS GRID */}
            <div className="grid gap-6 md:grid-cols-2">
              {/* CARD 1: APPLICANT INFORMATION */}
              <DetailCard icon={User} title="Applicant information">
                <DetailField icon={User} label="Full Name" value={data.name} full />
                <DetailField icon={CalendarDays} label="Age" value={data.age ? `${data.age} Yrs` : "—"} />
                <DetailField icon={CalendarDays} label="Date of Birth" value={data.date_of_birth} />
                <DetailField icon={Phone} label="Phone Number" value={data.mobile_number} />
                <DetailField icon={CircleDot} label="Recipient" value={data.tiffin_recipient} />
                <DetailField icon={Users} label="Monthly Income" value={data.monthly_income} full />
              </DetailCard>

              {/* CARD 2: ADDRESS & ARRANGEMENT */}
              <DetailCard icon={MapPin} title="Address & arrangement">
                <DetailField icon={Home} label="Full Address" value={data.address} full />
                <DetailField icon={Truck} label="Arrangement Details" value={data.tiffin_arrangement_details} full />
                <DetailField icon={FileText} label="Reason for Seva" value={data.reason_for_providing_tiffin} full />
              </DetailCard>

              {/* CARD 3: SEVA DETAILS */}
              <DetailCard icon={Layers} title="Seva details">
                <DetailField icon={Layers} label="Category" value={data.categorys} />
                <DetailField icon={Heart} label="Seva Category" value={data.seva_category} />
                {data.tiffin_type && <DetailField icon={CircleDot} label="Tiffin Seva Type" value={data.tiffin_type} />}
                <DetailField icon={CalendarDays} label="Tiffin Start Date" value={data.tiffin_start_date} />
                <DetailField icon={CalendarDays} label="Tiffin End Date" value={data.tiffin_end_date} />
              </DetailCard>

              {/* CARD 4: REFERENCE PROVIDER */}
              {data.categorys !== "Below 60 Years" && (
                <DetailCard icon={Users} title="Reference provider">
                  <DetailField icon={User} label="Reference Name" value={data.reference_provider_name} />
                  <DetailField icon={Phone} label="Reference Phone" value={data.reference_provider_mobile} />
                  <DetailField icon={Home} label="Reference Address" value={data.reference_provider_address} full />
                  <DetailField icon={Briefcase} label="Reference Occupation" value={data.reference_provider_occupation} full />
                </DetailCard>
              )}

              {/* CARD 5: IDENTITY & VERIFICATION (Full Width, Split layout) */}
              <div className="bg-white rounded-2xl border border-border/70 p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow sm:col-span-2">
                <div className="flex items-center gap-2.5 border-b border-border pb-3">
                  <div className="text-blue-600">
                    <FileCheck className="h-5 w-5" />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-foreground leading-none">Identity & verification</h3>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  {/* Left Side: Checklist and Labels */}
                  <div className="space-y-4">
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-2">
                        Checklist Documents
                      </span>
                      <div className="flex flex-wrap gap-1.5 pt-0.5">
                        {Array.isArray(data.identity_documents) && data.identity_documents.length > 0 ? (
                          data.identity_documents.map((doc: string, idx: number) => (
                            <Badge key={idx} variant="secondary" className="text-[10px] rounded-md font-semibold bg-secondary/80">
                              {doc}
                            </Badge>
                          ))
                        ) : typeof data.identity_documents === "string" && data.identity_documents.length > 0 ? (
                          String(data.identity_documents)
                            .replace(/[\[\]"]/g, "")
                            .split(",")
                            .map((doc: string, idx: number) => (
                              <Badge key={idx} variant="secondary" className="text-[10px] rounded-md font-semibold bg-secondary/80">
                                {doc.trim()}
                              </Badge>
                            ))
                        ) : (
                          <span className="text-muted-foreground italic text-[11px]">No docs selected</span>
                        )}
                      </div>
                    </div>

                    {/* <div>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block">
                        Identity Attachment
                      </span>
                      <span className="text-xs text-muted-foreground italic mt-1 block">
                        {data.identity_document ? "File uploaded and verified" : "No file attached"}
                      </span>
                    </div> */}
                  </div>

                  {/* Right Side: Uploaded Document view panel */}
                  <div className="flex flex-col justify-center items-start border-t md:border-t-0 md:border-l border-border/60 pt-4 md:pt-0 md:pl-6">
                    {data.identity_document ? (
                      <div className="space-y-3 w-full">
                        {/* {/\.(jpg|jpeg|png|gif|webp)/i.test(data.identity_document) ? (
                          <div className="relative rounded-xl border border-border overflow-hidden h-24 w-40 bg-muted mb-2">
                            <img
                              src={data.identity_document}
                              alt="Identity Proof"
                              className="object-cover w-full h-full"
                            />
                          </div>
                        ) : null} */}
                        <a
                          href={data.identity_document}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50/50 hover:bg-blue-50 text-blue-700 font-semibold px-4 py-2 text-xs transition-colors"
                        >
                          <Paperclip className="h-4 w-4" /> View Uploaded Document
                        </a>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-6 border border-dashed border-border rounded-xl w-full bg-secondary/5 text-muted-foreground text-xs italic">
                        No file attached
                      </div>
                    )}
                  </div>
                </div>
              </div>


            </div>

            {/* FAMILY MEMBERS CARD */}
            {data.categorys !== "Below 60 Years" && (
              <div className="bg-white rounded-2xl border border-border/70 p-6 space-y-4 shadow-sm">
                <div className="flex items-center gap-2.5 border-b border-border pb-3">
                  <div className="text-blue-600">
                    <Users className="h-5 w-5" />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-foreground leading-none">Family members profile</h3>
                </div>

                {(!data.family_members || (Array.isArray(data.family_members) && data.family_members.length === 0)) ? (
                  <p className="text-xs text-muted-foreground italic text-center py-2">
                    No family members declared.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-secondary/40 text-muted-foreground font-semibold border-b border-border">
                        <tr>
                          <th className="p-2.5 text-center w-12">No.</th>
                          <th className="p-2.5">Name</th>
                          <th className="p-2.5 w-16">Age</th>
                          <th className="p-2.5">Relationship</th>
                          <th className="p-2.5">Occupation & Mobile</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {(Array.isArray(data.family_members)
                          ? data.family_members
                          : typeof data.family_members === "string"
                            ? JSON.parse(data.family_members)
                            : []
                        ).map((member: any, idx: number) => (
                          <tr key={idx} className="hover:bg-secondary/10 transition-colors">
                            <td className="p-2.5 text-center font-semibold text-muted-foreground">{idx + 1}</td>
                            <td className="p-2.5 font-medium text-foreground">{member.name || "—"}</td>
                            <td className="p-2.5">{member.age || "—"}</td>
                            <td className="p-2.5">{member.relationship || "—"}</td>
                            <td className="p-2.5 text-muted-foreground">
                              {member.occupation || "—"} {member.mobile_number ? `(${member.mobile_number})` : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* CARD 6: PAYMENT HISTORY (Only for Below 60 Years) */}
            {data.categorys !== "Above 60 Years" && (
              <div className="bg-white rounded-2xl border border-border/70 p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow sm:col-span-2">
                <div className="flex items-center gap-2.5 border-b border-border pb-3">
                  <div className="text-blue-600">
                    <ClipboardList className="h-5 w-5" />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-foreground leading-none">Payment history</h3>
                </div>

                    <table className="w-full text-left text-xs">
                      <thead className="bg-secondary/40 text-muted-foreground font-semibold border-b border-border">
                        <tr>
                          <th className="p-3">Order ID</th>
                          <th className="p-3">Payment ID</th>
                          <th className="p-3">Method</th>
                          <th className="p-3">Reference</th>
                          <th className="p-3 text-center">Status</th>
                          <th className="p-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        <tr className="hover:bg-secondary/5 transition-colors">
                          <td className="p-3 font-medium text-foreground">
                            {data.razorpay_order_id || `OFFLINE-${data.id}`}
                          </td>
                          <td className="p-3 text-muted-foreground">
                            {data.razorpay_payment_id || "—"}
                          </td>
                          <td className="p-3">
                            {data.payment_mode || (data.entry_source === "ONLINE" ? "Online" : "Upi")}
                          </td>
                          <td className="p-3 text-muted-foreground">
                            {data.payment_reference || "—"}
                          </td>
                          <td className="p-3 text-center">
                            {data.payment_status === "PAID" || data.payment_status === "Paid" ? (
                              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white font-bold rounded-full px-2.5 py-0.5 shadow-none border-transparent">
                                Success
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-500 hover:bg-amber-500 text-white font-bold rounded-full px-2.5 py-0.5 shadow-none border-transparent">
                                Pending
                              </Badge>
                            )}
                          </td>
                          <td className="p-3 text-right font-bold text-foreground">
                            ₹{data.fee_amount ? Number(data.fee_amount).toLocaleString("en-IN") : "0"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
